import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database, Json } from '../generated';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type { OrderStatus } from './orders';

// ---------- wholesaler (the row itself) ---------------------------------

export type Wholesaler = Database['wholesalers']['Tables']['wholesalers']['Row'];
export type WholesalerInsert = Database['wholesalers']['Tables']['wholesalers']['Insert'];
export type WholesalerUpdate = Database['wholesalers']['Tables']['wholesalers']['Update'];
export type WholesalerSummary =
  Database['wholesalers']['Views']['v_wholesaler_summary']['Row'];
export type WholesalerStatus = Database['wholesalers']['Enums']['wholesaler_status'];
export type WholesalerTier = Database['wholesalers']['Enums']['wholesaler_tier'];
export type WholesalerPaymentTerms = Database['wholesalers']['Enums']['payment_terms'];

export interface ListWholesalersInput extends KeysetInput {
  search?: string;
  status?: WholesalerStatus;
  tier?: WholesalerTier;
  tag?: string;
  includeArchived?: boolean;
}

/**
 * Paginated keyset list of wholesalers in the active store. Mirrors
 * `listClients` -- see that function for the pagination contract details.
 */
export async function listWholesalers(
  ctx: ActiveContext,
  input: ListWholesalersInput = {},
): Promise<ListResult<Wholesaler>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = supabase()
    .schema('wholesalers')
    .from('wholesalers')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (!input.includeArchived) q = q.neq('status', 'archived');
  if (input.status) q = q.eq('status', input.status);
  if (input.tier) q = q.eq('tier', input.tier);
  if (input.tag) q = q.contains('tags', [input.tag]);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      [
        `company_name.ilike.${s}`,
        `display_name.ilike.${s}`,
        `doing_business_as.ilike.${s}`,
        `email.ilike.${s}`,
        `phone.ilike.${s}`,
        `account_number.ilike.${s}`,
      ].join(','),
    );
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as Wholesaler[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * One-shot bounded list, for dashboard widgets and pickers.
 */
export async function listWholesalersTop(
  ctx: ActiveContext,
  input: Omit<ListWholesalersInput, 'cursor' | 'countMode'> & { limit?: number } = {},
): Promise<Wholesaler[]> {
  const { rows } = await listWholesalers(ctx, {
    ...input,
    pageSize: input.limit ?? 25,
    countMode: 'none',
  });
  return rows;
}

export async function getWholesaler(
  ctx: ActiveContext,
  id: string,
): Promise<Wholesaler | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesalers')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export interface CreateWholesalerInput {
  companyName: string;
  displayName?: string;
  doingBusinessAs?: string | null;
  taxId?: string | null;
  resaleCertNumber?: string | null;
  resaleCertState?: string | null;
  resaleCertExpiresAt?: string | null;
  paymentTerms?: WholesalerPaymentTerms;
  creditLimitCents?: number;
  defaultCurrency?: string;
  tier?: WholesalerTier;
  pricingBookId?: string | null;
  primaryContactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  accountNumber?: string | null;
  notes?: string | null;
  tags?: string[];
  metadata?: Json;
  communicationPreferences?: Json;
  status?: WholesalerStatus;
}

function deriveDisplayName(
  input: CreateWholesalerInput | UpdateWholesalerInput,
): string | undefined {
  const trimmed = (v: string | null | undefined) => (v ?? '').trim();
  if (trimmed(input.displayName).length > 0) return trimmed(input.displayName);
  if (trimmed(input.doingBusinessAs).length > 0) return trimmed(input.doingBusinessAs);
  if (trimmed(input.companyName).length > 0) return trimmed(input.companyName);
  return undefined;
}

export async function createWholesaler(
  ctx: ActiveContext,
  input: CreateWholesalerInput,
): Promise<Wholesaler> {
  const { orgId, storeId } = requireContext(ctx);
  const company = (input.companyName ?? '').trim();
  if (!company) throw new Error('A wholesaler needs a company name.');
  const dn = deriveDisplayName(input) ?? company;
  const insert: WholesalerInsert = {
    org_id: orgId,
    store_id: storeId,
    company_name: company,
    display_name: dn,
    doing_business_as: input.doingBusinessAs ?? undefined,
    tax_id: input.taxId ?? undefined,
    resale_cert_number: input.resaleCertNumber ?? undefined,
    resale_cert_state: input.resaleCertState ?? undefined,
    resale_cert_expires_at: input.resaleCertExpiresAt ?? undefined,
    payment_terms: input.paymentTerms ?? 'prepay',
    credit_limit_cents: input.creditLimitCents ?? 0,
    default_currency: input.defaultCurrency ?? 'usd',
    tier: input.tier ?? 'bronze',
    pricing_book_id: input.pricingBookId ?? undefined,
    primary_contact_name: input.primaryContactName ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    website: input.website ?? undefined,
    account_number: input.accountNumber ?? undefined,
    notes: input.notes ?? undefined,
    tags: input.tags ?? [],
    metadata: input.metadata ?? {},
    communication_preferences: input.communicationPreferences ?? {},
    status: input.status ?? 'active',
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesalers')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data as Wholesaler;
}

export interface UpdateWholesalerInput {
  id: string;
  companyName?: string;
  displayName?: string;
  doingBusinessAs?: string | null;
  taxId?: string | null;
  resaleCertNumber?: string | null;
  resaleCertState?: string | null;
  resaleCertExpiresAt?: string | null;
  paymentTerms?: WholesalerPaymentTerms;
  creditLimitCents?: number;
  defaultCurrency?: string;
  tier?: WholesalerTier;
  pricingBookId?: string | null;
  primaryContactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  accountNumber?: string | null;
  notes?: string | null;
  tags?: string[];
  metadata?: Json;
  communicationPreferences?: Json;
  status?: WholesalerStatus;
}

export async function updateWholesaler(
  ctx: ActiveContext,
  input: UpdateWholesalerInput,
): Promise<Wholesaler> {
  requireContext(ctx);
  const patch: WholesalerUpdate = {};
  const touchedName =
    input.displayName !== undefined ||
    input.companyName !== undefined ||
    input.doingBusinessAs !== undefined;
  if (touchedName) {
    const dn = deriveDisplayName(input);
    if (dn !== undefined) patch.display_name = dn;
  }
  if (input.companyName !== undefined) patch.company_name = input.companyName;
  if (input.doingBusinessAs !== undefined) patch.doing_business_as = input.doingBusinessAs;
  if (input.taxId !== undefined) patch.tax_id = input.taxId;
  if (input.resaleCertNumber !== undefined) patch.resale_cert_number = input.resaleCertNumber;
  if (input.resaleCertState !== undefined) patch.resale_cert_state = input.resaleCertState;
  if (input.resaleCertExpiresAt !== undefined)
    patch.resale_cert_expires_at = input.resaleCertExpiresAt;
  if (input.paymentTerms !== undefined) patch.payment_terms = input.paymentTerms;
  if (input.creditLimitCents !== undefined) patch.credit_limit_cents = input.creditLimitCents;
  if (input.defaultCurrency !== undefined) patch.default_currency = input.defaultCurrency;
  if (input.tier !== undefined) patch.tier = input.tier;
  if (input.pricingBookId !== undefined) patch.pricing_book_id = input.pricingBookId;
  if (input.primaryContactName !== undefined)
    patch.primary_contact_name = input.primaryContactName;
  if (input.email !== undefined) patch.email = input.email;
  if (input.phone !== undefined) patch.phone = input.phone;
  if (input.website !== undefined) patch.website = input.website;
  if (input.accountNumber !== undefined) patch.account_number = input.accountNumber;
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.tags !== undefined) patch.tags = input.tags;
  if (input.metadata !== undefined) patch.metadata = input.metadata;
  if (input.communicationPreferences !== undefined)
    patch.communication_preferences = input.communicationPreferences;
  if (input.status !== undefined) patch.status = input.status;

  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesalers')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as Wholesaler;
}

export async function archiveWholesaler(ctx: ActiveContext, id: string): Promise<void> {
  await updateWholesaler(ctx, { id, status: 'archived' });
}

export async function getWholesalerSummary(
  ctx: ActiveContext,
  id: string,
): Promise<WholesalerSummary | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('v_wholesaler_summary')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// ---------- addresses ----------------------------------------------------

export type WholesalerAddress =
  Database['wholesalers']['Tables']['wholesaler_addresses']['Row'];
export type WholesalerAddressInsert =
  Database['wholesalers']['Tables']['wholesaler_addresses']['Insert'];

export async function listWholesalerAddresses(
  ctx: ActiveContext,
  wholesalerId: string,
): Promise<WholesalerAddress[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_addresses')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .order('is_default_shipping', { ascending: false })
    .order('is_default_billing', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertWholesalerAddressInput {
  id?: string;
  wholesalerId: string;
  label?: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  isDefaultBilling?: boolean;
  isDefaultShipping?: boolean;
  notes?: string | null;
}

export async function createWholesalerAddress(
  ctx: ActiveContext,
  input: UpsertWholesalerAddressInput,
): Promise<WholesalerAddress> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.isDefaultBilling) await clearDefault(ctx, input.wholesalerId, 'billing');
  if (input.isDefaultShipping) await clearDefault(ctx, input.wholesalerId, 'shipping');

  const row: WholesalerAddressInsert = {
    org_id: orgId,
    store_id: storeId,
    wholesaler_id: input.wholesalerId,
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_billing: Boolean(input.isDefaultBilling),
    is_default_shipping: Boolean(input.isDefaultShipping),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_addresses')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerAddress;
}

export async function updateWholesalerAddress(
  ctx: ActiveContext,
  input: UpsertWholesalerAddressInput & { id: string },
): Promise<WholesalerAddress> {
  requireContext(ctx);
  if (input.isDefaultBilling) await clearDefault(ctx, input.wholesalerId, 'billing', input.id);
  if (input.isDefaultShipping)
    await clearDefault(ctx, input.wholesalerId, 'shipping', input.id);

  const patch: Partial<WholesalerAddressInsert> = {
    label: input.label ?? 'primary',
    address_line1: input.addressLine1,
    address_line2: input.addressLine2 ?? undefined,
    city: input.city,
    state: input.state,
    postal_code: input.postalCode,
    country: input.country ?? 'US',
    is_default_billing: Boolean(input.isDefaultBilling),
    is_default_shipping: Boolean(input.isDefaultShipping),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_addresses')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerAddress;
}

export async function deleteWholesalerAddress(
  ctx: ActiveContext,
  id: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_addresses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

async function clearDefault(
  ctx: ActiveContext,
  wholesalerId: string,
  kind: 'billing' | 'shipping',
  exceptId?: string,
) {
  requireContext(ctx);
  const patch =
    kind === 'billing' ? { is_default_billing: false } : { is_default_shipping: false };
  const col = kind === 'billing' ? 'is_default_billing' : 'is_default_shipping';
  let q = supabase()
    .schema('wholesalers')
    .from('wholesaler_addresses')
    .update(patch)
    .eq('wholesaler_id', wholesalerId)
    .eq(col, true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

// ---------- contacts -----------------------------------------------------

export type WholesalerContact =
  Database['wholesalers']['Tables']['wholesaler_contacts']['Row'];
export type WholesalerContactInsert =
  Database['wholesalers']['Tables']['wholesaler_contacts']['Insert'];

export async function listWholesalerContacts(
  ctx: ActiveContext,
  wholesalerId: string,
): Promise<WholesalerContact[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_contacts')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

export interface UpsertWholesalerContactInput {
  id?: string;
  wholesalerId: string;
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean;
  notes?: string | null;
}

export async function createWholesalerContact(
  ctx: ActiveContext,
  input: UpsertWholesalerContactInput,
): Promise<WholesalerContact> {
  const { orgId, storeId } = requireContext(ctx);
  if (input.isPrimary) await clearPrimaryContact(ctx, input.wholesalerId);
  const row: WholesalerContactInsert = {
    org_id: orgId,
    store_id: storeId,
    wholesaler_id: input.wholesalerId,
    name: input.name,
    role: input.role ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    is_primary: Boolean(input.isPrimary),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_contacts')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerContact;
}

export async function updateWholesalerContact(
  ctx: ActiveContext,
  input: UpsertWholesalerContactInput & { id: string },
): Promise<WholesalerContact> {
  requireContext(ctx);
  if (input.isPrimary) await clearPrimaryContact(ctx, input.wholesalerId, input.id);
  const patch: Partial<WholesalerContactInsert> = {
    name: input.name,
    role: input.role ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    is_primary: Boolean(input.isPrimary),
    notes: input.notes ?? undefined,
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_contacts')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerContact;
}

export async function deleteWholesalerContact(
  ctx: ActiveContext,
  id: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_contacts')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

async function clearPrimaryContact(
  ctx: ActiveContext,
  wholesalerId: string,
  exceptId?: string,
) {
  requireContext(ctx);
  let q = supabase()
    .schema('wholesalers')
    .from('wholesaler_contacts')
    .update({ is_primary: false })
    .eq('wholesaler_id', wholesalerId)
    .eq('is_primary', true);
  if (exceptId) q = q.neq('id', exceptId);
  const { error } = await q;
  if (error) throw error;
}

// ---------- customer book (clients linked via portal) -------------------
//
// `wholesalers.v_wholesaler_client_directory` -- staff-side projection of the
// portal customer book: rows from `wholesalers.wholesaler_clients` joined
// with `clients.clients` (+ each client's default address). Populated from
// `apps/wholesale` via `wholesalers.rpc_portal_save_client`.

export interface WholesalerCustomer {
  link_id: string;
  org_id: string;
  store_id: string;
  wholesaler_id: string;
  client_id: string;
  is_primary: boolean;
  created_by_portal_user_id: string | null;
  created_by_portal_user_name: string | null;
  linked_at: string;
  archived_at: string | null;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  client_type: string;
  client_status: string;
  client_created_at: string;
  client_updated_at: string;
  default_address_id: string | null;
  default_address_label: string | null;
  default_address_line1: string | null;
  default_address_line2: string | null;
  default_city: string | null;
  default_state: string | null;
  default_postal_code: string | null;
  default_country: string | null;
  order_count: number;
  open_orders_count: number;
  fulfilled_orders_count: number;
}

/**
 * Look up one row of the wholesaler's customer book by `(wholesalerId,
 * clientId)`. Used by the per-customer detail page so the operator can
 * drill into a single portal-managed client from the Customers tab.
 *
 * Returns `null` if the pair isn't linked (or is archived). Same RLS path
 * as the list helper -- the view is `security_invoker = on`.
 */
export async function getWholesalerCustomer(
  ctx: ActiveContext,
  wholesalerId: string,
  clientId: string,
): Promise<WholesalerCustomer | null> {
  requireContext(ctx);
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .from('v_wholesaler_client_directory')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .eq('client_id', clientId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as WholesalerCustomer | null;
}

export async function listWholesalerCustomers(
  ctx: ActiveContext,
  wholesalerId: string,
): Promise<WholesalerCustomer[]> {
  requireContext(ctx);
  // Cast through `any` because `wholesalers.v_wholesaler_client_directory`
  // ships in migration 20260628020300 -- not yet baked into the
  // checked-in `generated.ts` (typegen runs against a local Supabase that
  // doesn't have this migration applied until the next `pnpm db:types`
  // pass). Runtime contract is `WholesalerCustomer[]`; verified via
  // Supabase MCP `execute_sql` post-deploy.
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .from('v_wholesaler_client_directory')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .order('is_primary', { ascending: false })
    .order('linked_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as WholesalerCustomer[];
}

// ---------- KPI snapshot (v_wholesaler_kpis) ----------------------------
//
// Migration 20260628020400. One-row-per-wholesaler aggregate read model
// powering the operator Overview tab KPI strip + tab-nav badges.

export interface WholesalerKpis {
  wholesaler_id: string;
  org_id: string;
  store_id: string;
  open_orders_count: number;
  open_ar_cents: number;
  mtd_revenue_cents: number;
  ytd_revenue_cents: number;
  last_order_at: string | null;
  customer_count: number;
  open_shipments_count: number;
}

export async function getWholesalerKpis(
  ctx: ActiveContext,
  wholesalerId: string,
): Promise<WholesalerKpis | null> {
  requireContext(ctx);
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .from('v_wholesaler_kpis')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as WholesalerKpis | null;
}

// ---------- shipments (v_wholesaler_shipments) --------------------------
//
// Migration 20260628020500. Wholesaler-scoped projection of
// `fulfillment.shipments` (which doesn't carry `wholesaler_id` natively --
// the view joins through `orders.orders`).

export type WholesalerShipmentStatus =
  | 'needs_label'
  | 'ready_to_pack'
  | 'packed'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'exception'
  | 'return_to_sender'
  | 'cancelled'
  | 'on_hold';

export interface WholesalerShipmentRow {
  /** Canonical row identity exposed by the view as both `id` (used by the
   *  shared keyset pagination helper) and `shipment_id` (legacy alias). */
  id: string;
  shipment_id: string;
  org_id: string;
  store_id: string;
  order_id: string;
  wholesaler_id: string;
  order_number: string;
  client_id: string | null;
  status: WholesalerShipmentStatus;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  label_id: string | null;
  label_cost_cents: number | null;
  ship_to_snapshot: Record<string, unknown> | null;
  parcel: Record<string, unknown> | null;
  weight_oz: number | null;
  packed_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  hold_until: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
  facility_scanned_at: string | null;
  facility_scanned_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListWholesalerShipmentsInput extends KeysetInput {
  wholesalerId: string;
  status?: WholesalerShipmentStatus | WholesalerShipmentStatus[];
  includeCancelled?: boolean;
}

export async function listWholesalerShipments(
  ctx: ActiveContext,
  input: ListWholesalerShipmentsInput,
): Promise<ListResult<WholesalerShipmentRow>> {
  requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = (supabase() as any)
    .schema('wholesalers')
    .from('v_wholesaler_shipments')
    .select('*', selectCountOption(input.countMode))
    .eq('wholesaler_id', input.wholesalerId);

  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  if (!input.includeCancelled) q = q.is('cancelled_at', null);

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as WholesalerShipmentRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

// ---------- AR aging (v_wholesaler_ar_aging) ----------------------------
//
// Migration 20260628020600. Open invoice rows per wholesaler with bucketed
// days-past-due. Used to render the AR aging pill on each row of the
// Orders tab + (eventually) an aggregate summary on Overview.

export type ArAgeBucket = 'current' | '0_30' | '31_60' | '61_90' | '90_plus';

export interface WholesalerArRow {
  order_id: string;
  wholesaler_id: string;
  org_id: string;
  store_id: string;
  order_number: string;
  status: OrderStatus;
  invoiced_at: string | null;
  due_at: string | null;
  total_cents: number;
  paid_cents: number;
  balance_cents: number;
  days_past_due: number;
  age_bucket: ArAgeBucket;
  created_at: string;
  updated_at: string;
}

export async function listWholesalerArAging(
  ctx: ActiveContext,
  wholesalerId: string,
  input: { limit?: number } = {},
): Promise<WholesalerArRow[]> {
  requireContext(ctx);
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .from('v_wholesaler_ar_aging')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .order('days_past_due', { ascending: false })
    .limit(input.limit ?? 500);
  if (error) throw error;
  return (data ?? []) as WholesalerArRow[];
}
